import { FC } from "react";
import { Link, LoaderFunction, useLoaderData } from "remix";
import { OptionalUser } from "~/lib/auth.server";
import { QuestionsContactUsBlurb, TryForFreeBlurb } from "~/lib/components";
import { BasicPage } from "~/lib/components/BasicPage";
import { FullUser } from "~/new_shared/db/user.server";
import { TntAllUrls } from "~/new_shared/urls";

export const ProseHeader: FC<{ id?: string }> = ({ children, id }) => (
  <h1 className="text-2xl mt-8" id={id}>
    {children}
  </h1>
);

interface LoaderData {
  user: FullUser | undefined;
}

export const loader: LoaderFunction = async ({ request }): Promise<LoaderData> => {
  const { user } = await OptionalUser(request);
  return { user };
};

export default function Faq() {
  const { user } = useLoaderData<LoaderData>();
  return (
    <BasicPage user={user}>
      <div className="max-w-prose mx-auto">
        <ProseHeader id="junkvsspam">Can ThxNoThx stop all junk email?</ProseHeader>
        <p>First some definitions:</p>
        <ul className="my-0">
          <li>
            <b>Spam</b>: unwanted emails sent by anyone that you have not had prior contact with/did
            not voluntarily give them your email address
          </li>
          <li>
            <b>Transactional emails*</b>: "facilitates an already agreed-upon transaction or updates
            a customer about an ongoing transaction". Basically, if you do business with a company,
            they can send you email about the purchases/services/etc. They cannot send you marketing
            material just because you purchased something. Some examples of transactional emails:
            <ul>
              <li>Receipts</li>
              <li>Updates about subscriptions</li>
              <li>Notices about purchases you've already made (eg info about a specific event).</li>
              <li>
                NOT marketing for other events or products you haven't previously signed up
                for/purchased
              </li>
            </ul>
          </li>
          <li>
            <b>Marketing/Commercial emails*</b>: "advertises or promotes a commercial product or
            service"
          </li>
        </ul>
        <small className="pl-4">* In the US, these are defined in the CAN-SPAM Act</small>
        <p>
          <b>
            ThxNoThx can help you reduce the amount of <u>marketing emails</u> you receive.
          </b>{" "}
          Once you click unsubscribe in your thxnothx inbox or you forward us an email from your
          email account to unsubscribe@, we will use the unsubscribe link in that email to
          unsubscribe you. If you indicate you want to unsubscribe from a company's marketing email
          multiple times, we will assume that there is a problem getting you unsubscribed and we
          will contact that company on your behalf to get you unsubscribed.
        </p>
        {/* 
        <p>
          ThxNoThx does give you a new option for fighting spam: If your
          ThxNoThx address for a specific company is leaked to spammers, you can
          just turn that email address off.
        </p>
        <p>
          As an example, I received a lot of spam sent to the email addresses I
          provided to MySpace, USPS, and Dropbox. I simply muted all email sent
          to those addresses and they don't bother me anymore.
        </p> */}
        <ProseHeader id="canspam">
          What is the CAN-SPAM Act? What about laws in other countries?
        </ProseHeader>
        <p>You can find more information about various countries' spam laws here:</p>
        <ul>
          <li>
            USA: CAN-SPAM ACT -{" "}
            <a href="https://www.privacypolicies.com/blog/can-spam">friendly visual intro</a>
            ,&nbsp;
            <a href="https://www.ftc.gov/tips-advice/business-center/guidance/can-spam-act-compliance-guide-business">
              official non-lawyer explanation (ftc.gov)
            </a>
            , or&nbsp;
            <a href="https://www.law.cornell.edu/wex/inbox/what_is_can-spam">legal explanation</a>
          </li>
          <li>
            <a href="https://www2.deloitte.com/ca/en/pages/risk/articles/canada-anti-spam-law-casl-faq.html">
              Canada: CASL
            </a>
          </li>
          <li>
            <a href="https://gdpr.eu/email-encryption/">EU GDPR: email section</a>
          </li>
        </ul>
        <ProseHeader id="whycharge">Why isn't this free?</ProseHeader>
        <p>
          There are costs associated with receiving, filtering, and storing emails for users. I have
          to cover those somehow, and the options are either to have ads or to charge for the
          service.
        </p>
        <p>
          Given that the general intent of ThxNoThx is to help you reduce the amount of marketing in
          your inbox (and thus your life), it seems contradictory for that service to be showing you
          ads :)
        </p>
        <p>
          If you'd really like a free, ad-supported subscription option, write us a note! email:
          adsplease at sunnyporch.dev. We might consider adding a free tier, but always plan to have
          a paid tier.
        </p>
        <ProseHeader id="idoitmyself"> Can't I just unsubscribe from emails myself?</ProseHeader>
        <p>
          You certainly can! However, in talking to friends and family I discovered that people
          don't really trust that unsubscribe works. I personally find it annoying to hunt for the
          tiny unsubscribe link and then navigate whatever nonsense a particular unsubscribe page
          has going on. I'm also never really sure if an unsubscribe really worked since it can take
          2 weeks to actually kick in, so it's hard to really trust it/feel like it's "solved" when
          I unsubscribe from an email list.
        </p>
        RealUnsubscribe has some clear advantages vs regular unsusbscribe:
        <ul>
          <li>
            Making unsubscribe truly one click from the inbox makes it so much easier to
            unsubscribe. You don't even have to open up the email!
          </li>
          <li>
            We will keep track of emails you mark for unsubscribe to see if more emails are sent by
            that company. If the unsubscribe attempt seems to have failed, we will go the extra mile
            and follow up with that company to get them to stop sending you email
          </li>
        </ul>
        <p>
          I also know from experience that sometimes unsubscribe functionality gets broken and the
          company may not realize it. Even for a well intentioned company, it's not a feature that's
          used internally, so you won't usually discover that it's broken. By tracking unsubscribe
          problems across multiple users, ThxNoThx can bring a bit more evidence to a conversation
          with an email sender that something is broken.
        </p>
        {/*
        <Header>Do you hate email marketers?</Header>
        <p>I'd call it a complex relationship.</p>
        <p>
          I built marketing email tools professionally for a few years, and I
          believe that marketing is important to every company and if we never
          heard about any products, then we would be missing out on tools that
          we could use to solve problems in our life.
        </p>
        <p>
          Because I worked on email marketing tools, I also know folks in the
          marketing email business - I still consider them friends now.
        </p>
        <p>
          But email marketing is an overused channel, and there's a lot of bad
          email marketing. I want to help users take control of their inbox and
          stop getting emails they don't want.
        </p>
        */}
        <ProseHeader id="wontmissimportant">
          If I unsubscribe, will I miss important emails?
        </ProseHeader>
        <p>
          The short answer is that you'll continue to get important emails like receipts and package
          tracking information, and only the advertising emails will stop.
        </p>
        <p>
          "Unsubscribing from emails" for almost all* companies means that you will stop receiving
          their marketing emails - anything that "advertises or promotes a commercial product or
          service".
        </p>
        <p>
          It's industry standard practice (and in the best interest of companies) to continue
          sending you transactional emails ("facilitates an already agreed-upon transaction or
          updates a customer about an ongoing transaction") regardless of whether you unsubscribe or
          not. US law (CAN-SPAM) specifically allows transactional emails to continue after the user
          unsubscribes from emails. If you really want to stop transactional emails you'll likely
          need to completely delete your account at a company.
        </p>
        <p>
          *A caveat: the behavior of unsubscribe is ultimately decided by individual companies, so
          there <i>might</i> be a few companies who will actually stop sending you any emails
          whatsoever, but we here at ThxNoThx haven't seen a case where someone has missed an
          important email because they've unsubscribed.
        </p>
        <ProseHeader id="rumagic">How does RealUnsubscribe work?</ProseHeader>
        <p>
          When you forward an email to unsubscribe@thxnothx.com or mark an email for unsubscribe in
          the UI, we take the following steps:
        </p>
        <ol>
          <li>
            Our system examines it to determine who sent it and if there are any unsubscribe
            headers/links in the email.
          </li>
          <li>
            If the automation cannot determine what the unsubscribe URL is, someone on our team will
            examine the email to see if they can find that information.
          </li>
          <li>
            The system will open that URL and complete the unsubscribe process for you. If our
            automation cannot complete that process automatically, a member of our team will
            complete that task.
          </li>
        </ol>
        <ProseHeader id="marketersheart">
          I'm an email marketer - why shouldn't I block emails in the @thxnothx.com domain?
        </ProseHeader>
        <p>
          I (Stephen, the founder of ThxNoThx) spent 2 years working on email marketing tools
          myself, as well as an additional year building email marekting &amp; notification services
          for a consumer-scale website. So I understand what your goals are and think that ThxNoThx
          represents a nice balance between email users' goals and marketing goals.
        </p>
        <p>Here's why you shouldn't block our email addresses:</p>
        <ol>
          <li>
            This is <b>not</b> a throwaway email service. Your users will always be reachable at the
            address that you give them, and emails sent to ThxNoThx addresses will end up in summary
            emails sent to the users.
          </li>
          <li>
            If you'd like, I'd be happy to provide open rate %s for emails from your service. This
            will be highly accurate since it will be based on our records of who opened emails sent
            by your service. Send an email to team (at) thxnothx.com and we'll get that process
            started. (If you're a user reading this: note that this will never be per-user data, the
            only information they will get is "10% of our users who received email X opened the
            email to read it.")
          </li>
        </ol>
        {/*
        <Header> What if a spammer gets my username?</Header>
        <p>
          eg. what if a spammer gets my thxnothx username and starts sending
          emails to other addresses in my domain?{" "}
        </p>
        <p>
          The plan is that we would create a second part of your email address,
          and that would need to be included in any new email addresses in your
          subdomain. So for example, instead of target@user.thxnothx.com, you
          could create the secret "blue", then any new email address you use
          would need to include that, so it'd be target_blue@user.thxnothx.com.{" "}
        </p>
        <p>
          Note that in this scheme, old email addresses without the secret would
          still work just fine, but any new addresses created would need to
          include the secret.
        </p>
        <p>We could also create a new username for you if you'd like.</p>
        */}
        <ProseHeader id="customdomain">
          I have my own domain, can you take over receiving emails at that domain?
        </ProseHeader>
        <p>
          We definitely plan to support that! Get in touch with us to discuss how to set that up:
          team (at) thxnothx (dot) com
        </p>
        {/*
        <Header>This is a new service - how do I know it won't go away?</Header>
        <p>
          Great question! I debated whether to include this in the FAQ since I
          wasn't sure if it would scare away potential users. I decided to be
          open about this possibility and hope you'll see this information as a
          sign that I've thought seriously about this service and intend to be
          open and honest with my users.
        </p>
        <p>
          Here's a couple things to consider when thinking about the potential
          longevity of this service:
        </p>
        <ul>
          <li>
            I use this service and my friends and family have{" "}
            <del>been forced to</del> eagerly signed up for the service as well.
            I will definitely hear about it if I break my wife's email.
          </li>
          <li>
            This is a paid service that charges enough to cover costs for each
            user.
          </li>
          <li>
            I have designed the service from the beginning to be lightweight to
            run &amp; maintain.
          </li>
          <li>
            I'm a professional software engineer with 20 years of experience
            keeping things running.
          </li>
        </ul>
        <p>
          Ultimately, no one can guarantee that their Internet Service will
          stick around. In the case that for whatever reason I have to shut down
          this service, here's what I intend to do:
        </p>
        <ul>
          <li>
            Give 30 days of warning before normal functionality of the site is
            turned off
          </li>
          <li>Switch the site to relay-only mode for an additional 60 days</li>
          <li>
            I will retain the domain name for at least 2 years after the site is
            shut down. This means that I will have ability to setup a particular
            email address to receive email if someone really desperately needs
            access to an email account for a particular address (eg. to recover
            an account.)
          </li>
        </ul>
            */}
        <ProseHeader id="passwords">How do I log into the site?</ProseHeader>
        <p>
          ThxNoThx doesn't use passwords for logging in - we send a "magic link" to your email
          address. To be sent a magic link:
          <ol>
            <li>
              Click "Sign in" at the top of the web site (if you don't see it, then you're already
              signed in) or <Link to={TntAllUrls.Login()}>Sign in now</Link>
            </li>
            <li>We'll send you a magic link to your non-thxnothx email address</li>
            <li>
              Once you click that link, you're logged in on that device and will remain logged in
              unless you use the "Sign out" option.
            </li>
          </ol>
        </p>
        <p>
          That's it! It's super simple. If you need to log in on multiple devices you can request a
          link on each device.
        </p>
        <p>
          I've been asked about how secure this login method is. It's just as secure as standard
          password login, since on those sites you can always just request a password reset link be
          sent to your email address. So in either system, if you can access the user's email inbox,
          you can access the user's account.
        </p>
        <ProseHeader id="bundlevsforward">Receive Actions</ProseHeader>
        <p>
          ThxNoThx provides a couple options for what happens when you receive an email at your
          ThxNoThx address:
        </p>
        <ul>
          <li>
            <b>Forward:</b> Emails will be immediately forwarded to your non-ThxNoThx email inbox.
            {/* TODO: these emails should not be shown in your TNT inbox*/}
          </li>
          <li>
            <b>Bundle:</b> You will get an email from ThxNoThx that contains a summary of all the
            bundled emails you've received. You can always view bundled emails in your inbox.
          </li>
          {/* <li><b>Unsubscribe:</b></li> */}
          <li>
            <b>Block:</b> Emails will be ignored by ThxNoThx and you won't see them in your
            inbox/summary emails/etc. This is different from unsubscribing from a sender: if you
            unsubscribe from a sender but then are sent another email, ThxNoThx will still show that
            email in the inbox (this is useful because you may receive emails about future business
            you do with a company), but no email from blocked senders will appear in your inbox.
          </li>
          <li>
            <b>Newsletter:</b> Emails will be shown in your Newsletters view rather than in your
            inbox.
          </li>
        </ul>
        <p>
          You can tell ThxNoThx to bundle/forward/block all emails for a particular sender (ie "all
          emails from walmart.com") as well as for your entire account ("I want all emails I receive
          to be bundled, unless I have a specific bundling setting for that sender")
        </p>
        {/* <ProseHeader id="freeTrialOver">
          What happens if I want to close my account or choose not to subscribe after my free trial
          is over?
        </ProseHeader>
        <ul>
          <li>Your ThxNoThx inbox will continue receiving new messages for 7 days</li>
          <li>You'll have 30 days of access to your ThxNoThx inbox</li>
          <li>
            We will not process any unsubscribe requests received after your free trial ended.
          </li>
        </ul> */}
        <TryForFreeBlurb user={user} />
        <QuestionsContactUsBlurb />
      </div>
    </BasicPage>
  );
}

/* 
They
          aren't aware that marketing emails are required by the{" "}
          <a href="https://www.privacypolicies.com/blog/can-spam">
            CAN-SPAM law here in the US (and other laws internationally)
          </a>{" "}
          to include an unsubscribe link, and that there are severe penalties
          ($16,000 per email in the US!) backing up those laws.
          
           <p>
          The intent of ThxNoThx is to be a inbox for all the less important
          email in your life. I do not intend for users to use the site for
          their most important accounts. As an example, I don't personally use
          this for my retirement accounts.
        </p>
          */

/* 
                <Header>Why should I trust ThxNoThX with my email?</Header>
        <p>Hi! I'm Stephen. </p>
        <p>
          I've worked with email as part of my job as a software engineer 3
          times:
        </p>

        <li>
          At Microsoft I worked on an email client called Windows Live Mail. You
          might also know it by it's original name: Outlook Express. While there
          I did major work on the MIME processing engine. MIME is the file
          format for emails. It's very old and email has 1000 use cases. This
          means that MIME is both powerful and complicated.
        </li>
        <li>
          At an email marketing startup called Appature, I lead a team building
          a tool that allows marketers to create sophisticated email campaigns
          that react to user behavior (eg. try out different ways to contact a
          particular user, and continue to contact them along mechanisms that
          work, or stop sending if they don't respond)
        </li>
        <li>
          At Google I worked on the Kaggle team, my last project there was
          expanding their notifications infrastructure, which includes the
          transactional emails Kaggle sent to users.
        </li>
        */
